import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Newspaper } from 'lucide-react';
import InteractiveArticleViewer from '../components/InteractiveArticleViewer';
import RelatedArticles from '../components/RelatedArticles';
import { ArticleComments, ShareWidget } from '../components/ArticleInteractions';

/**
 * JSU vs Alabama A&M - Game of the Week Interactive Article Page
 * 
 * Features the defensive matchup between Torenzo Quinn and Chris Shelling,
 * player cards, quarterback comparison, polls, and more.
 */
const JSUvsAAMUArticlePage = () => {
  const articleUrl = '/articles/jsu-aamu-game-of-week-2026.html';
  
  // Article metadata for social cards
  const articleMeta = {
    title: "SheLovesThee Game of the Week: Jackson State vs Alabama A&M",
    description: "The defensive minds collide. Torenzo Quinn vs Chris Shelling. Full breakdown of JSU vs AAMU with player cards, QB comparison, polls and more.",
    image: "https://images.sidearmdev.com/resize?url=https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/jacksonstateu.sidearmsports.com/images/2026/9/7/LOCKHART.png&width=600&type=webp",
    url: "https://shelovesthee.com/jsu-vs-aamu-2026",
    author: "Timona Stapleton | SheLovesThee",
    category: "Football"
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* SEO & Social Cards Meta Tags */}
      <Helmet>
        <title>{`${articleMeta.title} | SheLovesThee`}</title>
        <meta name="description" content={articleMeta.description} />
        <meta name="author" content={articleMeta.author} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={articleMeta.title} />
        <meta property="og:description" content={articleMeta.description} />
        <meta property="og:image" content={articleMeta.image} />
        <meta property="og:url" content={articleMeta.url} />
        <meta property="og:site_name" content="SheLovesThee" />
        <meta property="article:author" content={articleMeta.author} />
        <meta property="article:section" content={articleMeta.category} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={articleMeta.title} />
        <meta name="twitter:description" content={articleMeta.description} />
        <meta name="twitter:image" content={articleMeta.image} />
        <meta name="twitter:site" content="@SheLovesThee" />
      </Helmet>

      {/* Game of the Week Banner */}
      <div className="bg-gradient-to-r from-[#0b4e9b] via-yellow-500 to-[#8e0e2c] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
          <Newspaper className="w-5 h-5 animate-pulse" />
          <span className="font-bold text-sm uppercase tracking-wide">
            SheLovesThee Game of the Week: JSU vs Alabama A&M
          </span>
          <Newspaper className="w-5 h-5 animate-pulse" />
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="bg-[#071a3d] text-white py-3 px-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white hover:text-blue-300 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back to Home</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/news" 
              className="text-white hover:text-blue-300 transition-colors text-sm"
              data-testid="nav-news"
            >
              News
            </Link>
            <Link 
              to="/swac-pickem" 
              className="text-white hover:text-blue-300 transition-colors text-sm"
              data-testid="nav-pickem"
            >
              Pick'em
            </Link>
            <Link 
              to="/qb-analytics" 
              className="text-white hover:text-blue-300 transition-colors text-sm"
              data-testid="nav-qb"
            >
              QB Analytics
            </Link>
          </div>
        </div>
      </div>

      {/* Article Content - Full Width Container */}
      <main className="pb-8">
        <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
          <InteractiveArticleViewer 
            articleUrl={articleUrl}
            title={articleMeta.title}
          />
        </div>
        
        {/* Article Interactions Section - Comments & Share Only (Polls/Predictions are in the HTML article) */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Share & Info - Left Column */}
            <div className="lg:col-span-1">
              <ShareWidget 
                articleId="jsu-vs-aamu-2026"
                articleTitle={articleMeta.title}
                articleUrl={articleMeta.url}
                team1="Jackson State"
                team2="Alabama A&M"
              />
            </div>
            
            {/* Comments - Right Column (2 cols) */}
            <div className="lg:col-span-2">
              <ArticleComments 
                articleId="jsu-vs-aamu-2026"
                title="Game Day Discussion"
              />
            </div>
          </div>
        </div>
        
        {/* Related Articles Section */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 mt-12">
          <RelatedArticles 
            currentArticleId="jsu-vs-aamu-2026"
            category="Football"
            tags={["jackson state", "alabama a&m", "swac", "football", "game of the week"]}
            maxArticles={4}
            title="More Football Coverage"
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#071a3d] text-white py-6 text-center">
        <p className="text-gray-400 text-sm">
          SheLovesThee Sports Media • HBCU Sports. Real Talk. Thee I Love.
        </p>
        <p className="text-gray-500 text-xs mt-2">
          Photos courtesy of Jackson State Athletics and Alabama A&M Athletics
        </p>
      </footer>
    </div>
  );
};

export default JSUvsAAMUArticlePage;
